import { Play, Pause, SkipBack, SkipForward, Square, Repeat, ChevronDown, X } from 'lucide-react';
import { usePlayer, formatTime } from '@/context/PlayerContext';
import { cn } from '@/lib/utils';

const RATES = [0.5, 0.75, 1, 1.25, 1.5];

export function FullPlayer() {
  const {
    currentTrack,
    playbackState,
    currentTime,
    duration,
    playbackRate,
    loop,
    isPlayerVisible,
    isFullScreen,
    pause,
    resume,
    stop,
    seek,
    seekRelative,
    setPlaybackRate,
    setLoopPointA,
    setLoopPointB,
    clearLoop,
    setFullScreen,
    closePlayer,
  } = usePlayer();

  if (!isPlayerVisible || !currentTrack || !isFullScreen) return null;

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const isPlaying = playbackState === 'playing';
  const isLoading = playbackState === 'loading';

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    seek(percent * duration);
  };

  return (
    <div className="lg:hidden fixed inset-0 bg-bg-base z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-subtle">
        <button
          onClick={() => setFullScreen(false)}
          className="p-2 -ml-2 text-text-secondary"
        >
          <ChevronDown className="w-6 h-6" />
        </button>
        <span className="text-sm font-medium text-text-secondary">Nu aan het afspelen</span>
        <button
          onClick={closePlayer}
          className="p-2 -mr-2 text-text-secondary"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Cover */}
      <div className="flex-1 flex items-center justify-center p-8">
        {currentTrack.coverUrl ? (
          <img
            src={currentTrack.coverUrl}
            alt={currentTrack.title}
            className="w-full max-w-[300px] aspect-square rounded-lg object-cover shadow-theme-xl"
          />
        ) : (
          <div className="w-full max-w-[300px] aspect-square rounded-lg bg-bg-elevated flex items-center justify-center">
            <span className="text-8xl text-zzing-purple-500">♪</span>
          </div>
        )}
      </div>

      {/* Track info */}
      <div className="px-6 text-center">
        <h2 className="text-xl font-bold text-text-primary truncate">
          {currentTrack.title}
        </h2>
        <p className="text-text-secondary mt-1">
          {currentTrack.voicePart || currentTrack.artist}
        </p>
      </div>

      {/* Progress bar */}
      <div className="px-6 mt-6">
        <div
          className="h-2 bg-bg-input rounded-full cursor-pointer"
          onClick={handleProgressClick}
        >
          {/* Loop region */}
          {loop.pointA !== null && loop.pointB !== null && (
            <div
              className="absolute h-2 bg-zzing-purple-500/30 rounded-full"
              style={{
                left: `${(loop.pointA / duration) * 100}%`,
                width: `${((loop.pointB - loop.pointA) / duration) * 100}%`,
              }}
            />
          )}
          <div
            className="h-full bg-zzing-purple-500 rounded-full relative"
            style={{ width: `${progressPercent}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-md" />
          </div>
        </div>
        <div className="flex justify-between text-xs text-text-muted mt-2 font-mono">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Main controls */}
      <div className="flex items-center justify-center gap-6 py-6">
        <button
          onClick={() => seekRelative(-10)}
          className="p-3 text-text-secondary"
        >
          <SkipBack className="w-6 h-6" />
        </button>

        <button
          onClick={stop}
          className="p-3 text-text-secondary"
        >
          <Square className="w-6 h-6" />
        </button>

        <button
          onClick={() => isPlaying ? pause() : resume()}
          className="p-4 rounded-full bg-zzing-purple-500 text-white"
        >
          {isLoading ? (
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-8 h-8" />
          ) : (
            <Play className="w-8 h-8 ml-1" />
          )}
        </button>

        <button
          onClick={() => seekRelative(10)}
          className="p-3 text-text-secondary"
        >
          <SkipForward className="w-6 h-6" />
        </button>
      </div>

      {/* Speed control */}
      <div className="flex items-center justify-center gap-2 px-6">
        <span className="text-xs text-text-muted mr-2">Snelheid:</span>
        {RATES.map(rate => (
          <button
            key={rate}
            onClick={() => setPlaybackRate(rate)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              playbackRate === rate
                ? 'bg-zzing-purple-500 text-white'
                : 'bg-bg-elevated text-text-secondary'
            )}
          >
            {rate}x
          </button>
        ))}
      </div>

      {/* A-B Loop controls */}
      <div className="flex items-center justify-center gap-3 px-6 py-4 mb-safe">
        <span className="text-xs text-text-muted flex items-center gap-1">
          <Repeat className="w-4 h-4" />
          A-B:
        </span>
        <button
          onClick={setLoopPointA}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            loop.pointA !== null
              ? 'bg-green-500/20 text-green-400 border border-green-500/50'
              : 'bg-bg-elevated text-text-secondary'
          )}
        >
          A {loop.pointA !== null && `(${formatTime(loop.pointA)})`}
        </button>
        <button
          onClick={setLoopPointB}
          disabled={loop.pointA === null}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            loop.pointB !== null
              ? 'bg-red-500/20 text-red-400 border border-red-500/50'
              : loop.pointA === null
                ? 'bg-bg-elevated text-text-muted'
                : 'bg-bg-elevated text-text-secondary'
          )}
        >
          B {loop.pointB !== null && `(${formatTime(loop.pointB)})`}
        </button>
        {(loop.pointA !== null || loop.pointB !== null) && (
          <button
            onClick={clearLoop}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-bg-elevated text-text-secondary"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
